// MANTENER SINCRONIZADO con emmi-backend/src/game-quiz-attempt/*
// y con prisma model GameQuizAttempt.

import { GameWithTags } from "../gameQuiz/gameQuiz.types";

export type AttemptStatus = "IN_PROGRESS" | "COMPLETED" | "ABANDONED";

export const ATTEMPT_STATUS_VALUES: AttemptStatus[] = [
  "IN_PROGRESS",
  "COMPLETED",
  "ABANDONED",
];

/**
 * Qué es el quiz para el alumno, según el recurso del que cuelga (SCRUM-1167):
 * un video (VIDEOQUIZ), una lectura (READING) o ninguno de los dos (QUIZ).
 * Ver emmi-backend/src/game-quiz/game-quiz-kind.ts.
 */
export type GameQuizKind = "QUIZ" | "VIDEOQUIZ" | "READING";

export interface AttemptUserRef {
  id: number;
  first_name: string;
  last_name: string;
  username: string | null;
  email: string;
}

export interface AttemptGameQuizRef {
  id: number;
  title: string;
}

export interface AttemptHomeworkRef {
  id: number;
  title?: string;
}

/**
 * Veredicto persistido de una pregunta de un intento.
 *
 * Lo calcula el backend con el scorer del SDK al completar, no lo reporta el
 * cliente. A diferencia de `answers`, estas filas son inmutables: siguen
 * significando lo mismo aunque después se edite o se borre el quiz.
 *
 * Ausente en los intentos anteriores a SCRUM-651.
 */
export interface AttemptQuestionResult {
  id: number;
  question_index: number;
  game_id: string;
  game_type: string;
  question_text: string;
  answered: boolean;
  correct: boolean;
  /** Crédito parcial 0..1. `correct` es el booleano estricto, no este redondeado. */
  score: number;
  answer_raw: unknown;
}

// Row shape returned by GET /game-quiz-attempts/admin (list view).
export interface AttemptListItem {
  id: number;
  user_id: number;
  game_quiz_id: number;
  homework_id: number | null;
  status: AttemptStatus;
  current_question: number | null;
  total_points: number | null;
  score: number | null;
  correct_count: number | null;
  total_count: number | null;
  /** Intento número N del alumno para este quiz. Null en los históricos sin backfill. */
  attempt_number: number | null;
  started_at: string;
  last_saved_at: string;
  completed_at: string | null;
  user: AttemptUserRef;
  game_quiz: AttemptGameQuizRef;
  homework: AttemptHomeworkRef | null;
  kind: GameQuizKind;
}

// Paginated envelope (matches backend adminList return: { items, total, page, pageSize }).
export interface PaginatedAttemptsResponse {
  items: AttemptListItem[];
  total: number;
  page: number;
  pageSize: number;
}

// Detail shape returned by GET /game-quiz-attempts/admin/:id.
// `answers` and `ai_verdicts` are stored as JSON — we keep them loosely typed
// and let the display layer format each entry per game.type.
export interface AttemptDetail {
  id: number;
  user_id: number;
  game_quiz_id: number;
  homework_id: number | null;
  status: AttemptStatus;
  answers: Record<string, unknown> | null;
  ai_verdicts: Record<string, unknown> | null;
  current_question: number | null;
  total_points: number | null;
  score: number | null;
  correct_count: number | null;
  total_count: number | null;
  duration_sec: number | null;
  attempt_number: number | null;
  /**
   * Definición de los juegos tal como estaba al completar el intento. Cuando
   * está, manda sobre `game_quiz.games`: es la única lectura fiel de un intento
   * cuyo quiz se editó después.
   */
  quiz_snapshot: GameWithTags[] | null;
  /** Cuántas veces entró este alumno a resolver este quiz (incluye este intento). */
  total_attempts: number;
  question_results: AttemptQuestionResult[];
  started_at: string;
  last_saved_at: string;
  completed_at: string | null;
  user: AttemptUserRef;
  game_quiz: AttemptGameQuizRef & { games: GameWithTags[] | null };
  homework: AttemptHomeworkRef | null;
}

export interface ListAttemptsParams {
  status?: AttemptStatus;
  userId?: number;
  /** Texto libre: nombre, apellido, username o email del alumno (SCRUM-1095). */
  userSearch?: string;
  gameQuizId?: number;
  classroomId?: number;
  kind?: GameQuizKind;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}
